import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaDownload, FaEye, FaSignOutAlt, FaUser } from 'react-icons/fa';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [statistics, setStatistics] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [submissionsByUser, setSubmissionsByUser] = useState({});
  const [expandedUsers, setExpandedUsers] = useState({});
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [accommodations, setAccommodations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adminUsername, setAdminUsername] = useState('');
  
  // Filter and Sort States
  const [regFilters, setRegFilters] = useState({ name: '', email: '', affiliation: '', title: '' });
  const [regSort, setRegSort] = useState({ field: 'registrationDate', order: 'desc' });
  
  const [subFilters, setSubFilters] = useState({ username: '', email: '', affiliation: '' });
  const [subSort, setSubSort] = useState({ field: 'submissionDate', order: 'desc' });
  
  const [payFilters, setPayFilters] = useState({ name: '', email: '', status: '' });
  const [paySort, setPaySort] = useState({ field: 'registrationDate', order: 'desc' });
  
  const [accFilters, setAccFilters] = useState({ name: '', email: '', status: '' });
  const [accSort, setAccSort] = useState({ field: 'accommodationDate', order: 'desc' });
  
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const username = localStorage.getItem('adminUsername');

    if (!token) {
      navigate('/login');
      return;
    }

    setAdminUsername(username);
    fetchDashboardData(token);
  }, [navigate]);

  const fetchDashboardData = async (token) => {
    try {
      setLoading(true);
      setError('');

      // Fetch statistics
      const statsResponse = await fetch(`${process.env.REACT_APP_API_URL}/admin/statistics`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!statsResponse.ok) throw new Error('Failed to fetch statistics');
      const statsData = await statsResponse.json();
      setStatistics(statsData.statistics);

      // Fetch analytics
      const analyticsResponse = await fetch(`${process.env.REACT_APP_API_URL}/admin/analytics`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!analyticsResponse.ok) throw new Error('Failed to fetch analytics');
      const analyticsData = await analyticsResponse.json();
      setAnalytics(analyticsData.analytics);

      // Fetch registrations
      const regsResponse = await fetch(`${process.env.REACT_APP_API_URL}/admin/registrations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!regsResponse.ok) throw new Error('Failed to fetch registrations');
      const regsData = await regsResponse.json();
      setRegistrations(regsData.data);

      // Fetch submissions
      const subsResponse = await fetch(`${process.env.REACT_APP_API_URL}/admin/submissions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!subsResponse.ok) throw new Error('Failed to fetch submissions');
      const subsData = await subsResponse.json();
      setSubmissions(subsData.data);
      
      // Group submissions by userId for admin view
      const grouped = {};
      subsData.data.forEach(sub => {
        if (!grouped[sub.userId]) {
          grouped[sub.userId] = {
            userId: sub.userId,
            username: sub.username,
            email: sub.email,
            phone: sub.phone,
            title: sub.title,
            lastName: sub.lastName,
            affiliation: sub.affiliation,
            submissions: []
          };
        }
        grouped[sub.userId].submissions.push(sub);
      });
      
      // Sort submissions for each user by date (newest first)
      Object.keys(grouped).forEach(userId => {
        grouped[userId].submissions.sort((a, b) => new Date(b.submissionDate) - new Date(a.submissionDate));
      });
      
      setSubmissionsByUser(grouped);

      // Fetch conference registrations (payment details)
      const paymentResponse = await fetch(`${process.env.REACT_APP_API_URL}/admin/conference-registrations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!paymentResponse.ok) throw new Error('Failed to fetch payment details');
      const paymentData = await paymentResponse.json();
      setPaymentDetails(paymentData.data);

      // Fetch accommodations
      const accommodationResponse = await fetch(`${process.env.REACT_APP_API_URL}/admin/accommodations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!accommodationResponse.ok) throw new Error('Failed to fetch accommodations');
      const accommodationData = await accommodationResponse.json();
      setAccommodations(accommodationData.data);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/registrations/download/csv`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to download CSV');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `registrations_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download CSV');
      console.error('Download error:', err);
    }
  };

  const handleDownloadSubmissionsCSV = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/submissions/download-all/csv`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to download submissions CSV');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `submissions_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download submissions CSV');
      console.error('Download error:', err);
    }
  };

  const handleDownloadPaymentCSV = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/conference-registrations/download/csv`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to download payment details CSV');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payment_details_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download payment details CSV');
      console.error('Download error:', err);
    }
  };

  const handleDownloadAccommodationsCSV = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/accommodations/download/csv`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to download accommodations CSV');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `accommodations_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download accommodations CSV');
      console.error('Download error:', err);
    }
  };

  const handleVerifyPayment = async (registrationId, action) => {
    const token = localStorage.getItem('adminToken');
    try {
      setError('');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/conference-registrations/${registrationId}/verify-payment`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: action }),
      });

      if (!response.ok) throw new Error('Failed to verify payment');

      const result = await response.json();
      
      // Refresh payment details
      const regResponse = await fetch(`${process.env.REACT_APP_API_URL}/admin/conference-registrations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (regResponse.ok) {
        const regData = await regResponse.json();
        setPaymentDetails(regData.data);
      }
      
      // Show success message
      alert(`Payment status updated to ${action} successfully!`);
    } catch (err) {
      setError(`Failed to update payment status: ${err.message}`);
      console.error('Verify payment error:', err);
    }
  };

  const handleUpdateAccommodationStatus = async (accommodationId, status) => {
    const token = localStorage.getItem('adminToken');
    try {
      setError('');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/accommodations/${accommodationId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) throw new Error('Failed to update accommodation status');

      // Refresh accommodations
      const accResponse = await fetch(`${process.env.REACT_APP_API_URL}/admin/accommodations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (accResponse.ok) {
        const accData = await accResponse.json();
        setAccommodations(accData.data);
      }
      
      // Show success message
      alert(`Accommodation status updated to ${status} successfully!`);
    } catch (err) {
      setError(`Failed to update accommodation status: ${err.message}`);
      console.error('Update accommodation status error:', err);
    }
  };

  const handleDownloadPaymentProof = async (registrationId, fileName) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/conference-registrations/download-proof/${registrationId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'payment_proof');
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (err) {
      setError(`Failed to download payment proof: ${err.message}`);
      console.error('Download proof error:', err);
    }
  };

  const handleDownloadAccommodationProof = async (accommodationId, fileName) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/accommodations/download-proof/${accommodationId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'accommodation_proof');
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (err) {
      setError(`Failed to download accommodation proof: ${err.message}`);
      console.error('Download accommodation proof error:', err);
    }
  };

  const handleLogout = () => {
    // Show logout success notification
    alert('✓ Admin logged out successfully');
    
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
    navigate('/admin/login');
  };

  // Utility functions for filtering and sorting
  const filterRegistrations = (data) => {
    return data.filter(reg => {
      const fullName = `${reg.lastName || ''} ${reg.nameInitials || ''}`.toLowerCase();
      return (
        fullName.includes(regFilters.name.toLowerCase()) &&
        (reg.email || '').toLowerCase().includes(regFilters.email.toLowerCase()) &&
        (reg.affiliation || '').toLowerCase().includes(regFilters.affiliation.toLowerCase()) &&
        (reg.title || '').toLowerCase().includes(regFilters.title.toLowerCase())
      );
    });
  };

  const sortRegistrations = (data) => {
    const sorted = [...data];
    sorted.sort((a, b) => {
      let aVal, bVal;
      
      switch(regSort.field) {
        case 'registrationDate':
          aVal = new Date(a.registrationDate);
          bVal = new Date(b.registrationDate);
          break;
        case 'name':
          aVal = `${a.lastName} ${a.nameInitials}`;
          bVal = `${b.lastName} ${b.nameInitials}`;
          break;
        case 'email':
          aVal = a.email;
          bVal = b.email;
          break;
        default:
          return 0;
      }
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      return regSort.order === 'asc' ? 
        (aVal < bVal ? -1 : aVal > bVal ? 1 : 0) :
        (aVal > bVal ? -1 : aVal < bVal ? 1 : 0);
    });
    return sorted;
  };

  const filterSubmissions = (data) => {
    return Object.values(data).filter((userGroup) => {
      const username = (userGroup.username || '').toLowerCase();
      const email = (userGroup.email || '').toLowerCase();
      const affiliation = (userGroup.affiliation || '').toLowerCase();

      return (
        username.includes(subFilters.username.toLowerCase()) &&
        email.includes(subFilters.email.toLowerCase()) &&
        affiliation.includes(subFilters.affiliation.toLowerCase())
      );
    });
  };

  const sortSubmissions = (data) => {
    return [...data].sort((userA, userB) => {
      let aVal, bVal;

      switch(subSort.field) {
        case 'submissionDate':
          aVal = new Date(userA.submissions[0].submissionDate);
          bVal = new Date(userB.submissions[0].submissionDate);
          break;
        case 'username':
          aVal = userA.username.toLowerCase();
          bVal = userB.username.toLowerCase();
          break;
        case 'count':
          aVal = userA.submissions.length;
          bVal = userB.submissions.length;
          break;
        default:
          return 0;
      }

      return subSort.order === 'asc' ?
        (aVal < bVal ? -1 : aVal > bVal ? 1 : 0) :
        (aVal > bVal ? -1 : aVal < bVal ? 1 : 0);
    });
  };

  const filterPayments = (data) => {
    return data.filter(payment => {
      const fullName = `${payment.title || ''} ${payment.lastName || ''}`.toLowerCase();
      return (
        fullName.includes(payFilters.name.toLowerCase()) &&
        (payment.email || '').toLowerCase().includes(payFilters.email.toLowerCase()) &&
        (payment.registrationStatus || '').toLowerCase().includes(payFilters.status.toLowerCase())
      );
    });
  };

  const sortPayments = (data) => {
    const sorted = [...data];
    sorted.sort((a, b) => {
      let aVal, bVal;
      
      switch(paySort.field) {
        case 'registrationDate':
          aVal = new Date(a.registrationDate);
          bVal = new Date(b.registrationDate);
          break;
        case 'name':
          aVal = `${a.title} ${a.lastName}`;
          bVal = `${b.title} ${b.lastName}`;
          break;
        case 'totalAmount':
          aVal = a.totalAmount;
          bVal = b.totalAmount;
          break;
        case 'status':
          aVal = a.registrationStatus;
          bVal = b.registrationStatus;
          break;
        default:
          return 0;
      }
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      return paySort.order === 'asc' ? 
        (aVal < bVal ? -1 : aVal > bVal ? 1 : 0) :
        (aVal > bVal ? -1 : aVal < bVal ? 1 : 0);
    });
    return sorted;
  };

  const filterAccommodations = (data) => {
    return data.filter(acc => {
      const fullName = `${acc.title || ''} ${acc.lastName || ''}`.toLowerCase();
      return (
        fullName.includes(accFilters.name.toLowerCase()) &&
        (acc.email || '').toLowerCase().includes(accFilters.email.toLowerCase()) &&
        (acc.accommodationStatus || '').toLowerCase().includes(accFilters.status.toLowerCase())
      );
    });
  };

  const sortAccommodations = (data) => {
    const sorted = [...data];
    sorted.sort((a, b) => {
      let aVal, bVal;
      
      switch(accSort.field) {
        case 'accommodationDate':
          aVal = new Date(a.accommodationDate);
          bVal = new Date(b.accommodationDate);
          break;
        case 'name':
          aVal = `${a.title} ${a.lastName}`;
          bVal = `${b.title} ${b.lastName}`;
          break;
        case 'accommodationAmount':
          aVal = a.accommodationAmount;
          bVal = b.accommodationAmount;
          break;
        case 'status':
          aVal = a.accommodationStatus;
          bVal = b.accommodationStatus;
          break;
        default:
          return 0;
      }
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      return accSort.order === 'asc' ? 
        (aVal < bVal ? -1 : aVal > bVal ? 1 : 0) :
        (aVal > bVal ? -1 : aVal < bVal ? 1 : 0);
    });
    return sorted;
  };
  const viewSubmissionPDF = async (submissionId, fileName) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/submissions/view/${submissionId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to view PDF');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (err) {
      setError('Failed to view PDF');
      console.error('View error:', err);
    }
  };

  const downloadSubmissionPDF = async (submissionId, fileName) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/submissions/download/${submissionId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to download PDF');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download PDF');
      console.error('Download error:', err);
    }
  };
  if (loading) {
    return (
      <div className="admin-dashboard-container">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  // Apply filters and sorting
  const filteredAndSortedRegistrations = sortRegistrations(filterRegistrations(registrations));
  const filteredAndSortedSubmissions = sortSubmissions(filterSubmissions(submissionsByUser));
  const filteredAndSortedPayments = sortPayments(filterPayments(paymentDetails));
  const filteredAndSortedAccommodations = sortAccommodations(filterAccommodations(accommodations));

  return (
    <div className="admin-dashboard-container">
      {/* Header */}
      <header className="admin-header">
        <div className="header-content">
          <div className="header-title-section">
            <FaUser className="header-icon" />
            <h1>Admin Dashboard</h1>
          </div>
          <div className="header-actions">
            <span className="admin-username">Welcome, {adminUsername}</span>
            <button className="admin-logout-btn" onClick={handleLogout}>
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-content">
        {error && <div className="error-alert">{error}</div>}

        {/* SECTION 1: ALL REGISTRATIONS TABLE */}
        {filteredAndSortedRegistrations.length > 0 && (
          <div className="analytics-section full-width">
            <h2>All Signins ({filteredAndSortedRegistrations.length})</h2>
            
            {/* Filter Controls */}
            <div className="filter-controls">
              <input
                type="text"
                placeholder="Filter by Full Name..."
                value={regFilters.name}
                onChange={(e) => setRegFilters({...regFilters, name: e.target.value})}
                className="filter-input"
              />
              <input
                type="text"
                placeholder="Filter by Email..."
                value={regFilters.email}
                onChange={(e) => setRegFilters({...regFilters, email: e.target.value})}
                className="filter-input"
              />
              <input
                type="text"
                placeholder="Filter by Affiliation..."
                value={regFilters.affiliation}
                onChange={(e) => setRegFilters({...regFilters, affiliation: e.target.value})}
                className="filter-input"
              />
              <input
                type="text"
                placeholder="Filter by Title..."
                value={regFilters.title}
                onChange={(e) => setRegFilters({...regFilters, title: e.target.value})}
                className="filter-input"
              />
              <select 
                value={`${regSort.field}-${regSort.order}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setRegSort({ field, order });
                }}
                className="sort-select"
              >
                <option value="registrationDate-desc">Latest Registration First</option>
                <option value="registrationDate-asc">Oldest Registration First</option>
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
              </select>
              <button onClick={() => setRegFilters({ name: '', email: '', affiliation: '', title: '' })} className="clear-filter-btn">Clear Filters</button>
            </div>

            <div className="table-scroll">
              <table className="data-table full-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Full Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Affiliation</th>
                    <th>Registration Date</th>
                    <th>Submission</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedRegistrations.map((reg, idx) => (
                    <tr key={idx}>
                      <td>{reg.id}</td>
                      <td>{reg.title}</td>
                      <td><strong>{reg.lastName} {reg.nameInitials}</strong></td>
                      <td>{reg.email}</td>
                      <td>{reg.phone || '-'}</td>
                      <td>{reg.affiliation}</td>
                      <td>{new Date(reg.registrationDate).toLocaleDateString()}</td>
                      <td>
                        <span className={`submission-badge ${reg.hasSubmission === 'Yes' ? 'submitted' : 'pending'}`}>
                          {reg.hasSubmission}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SECTION 2: ALL SUBMISSIONS TABLE - NO HORIZONTAL SCROLL */}
        {filteredAndSortedSubmissions.length > 0 && (
          <div className="analytics-section full-width">
            <div className="submissions-header">
              <h2>All User Submissions ({filteredAndSortedSubmissions.length} users, {filteredAndSortedSubmissions.reduce((sum, u) => sum + u.submissions.length, 0)} total)</h2>
            </div>

            {/* Filter Controls */}
            <div className="filter-controls">
              <input
                type="text"
                placeholder="Filter by Username..."
                value={subFilters.username}
                onChange={(e) => setSubFilters({...subFilters, username: e.target.value})}
                className="filter-input"
              />
              <input
                type="text"
                placeholder="Filter by Email..."
                value={subFilters.email}
                onChange={(e) => setSubFilters({...subFilters, email: e.target.value})}
                className="filter-input"
              />
              <input
                type="text"
                placeholder="Filter by Affiliation..."
                value={subFilters.affiliation}
                onChange={(e) => setSubFilters({...subFilters, affiliation: e.target.value})}
                className="filter-input"
              />
              <select 
                value={`${subSort.field}-${subSort.order}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSubSort({ field, order });
                }}
                className="sort-select"
              >
                <option value="submissionDate-desc">Latest Submission First</option>
                <option value="submissionDate-asc">Oldest Submission First</option>
                <option value="username-asc">Username (A-Z)</option>
                <option value="count-desc">Most Submissions First</option>
              </select>
              <button onClick={() => setSubFilters({ username: '', email: '', affiliation: '' })} className="clear-filter-btn">Clear Filters</button>
            </div>

            <div className="submissions-table-wrapper">
              <table className="data-table submissions-table no-scroll">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Full Name</th>
                    <th>Email</th>
                    <th>Title</th>
                    <th>Affiliation</th>
                    <th>Submissions Count</th>
                    <th>Latest Submission File</th>
                    <th>Latest Submission Date</th>
                    <th>All Versions</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedSubmissions.map((userGroup) => {
                    const latestSub = userGroup.submissions[0];
                    const isExpanded = expandedUsers[userGroup.userId];
                    
                    return (
                      <React.Fragment key={userGroup.userId}>
                        <tr className="user-submission-row">
                          <td><strong>{userGroup.username}</strong></td>
                          <td><strong>{userGroup.lastName}</strong></td>
                          <td>{userGroup.email}</td>
                          <td>{userGroup.title}</td>
                          <td>{userGroup.affiliation}</td>
                          <td>
                            <span className="submission-count-badge">
                              {userGroup.submissions.length}
                            </span>
                          </td>
                          <td>
                            <code className="filename">{latestSub.fileName}</code>
                          </td>
                          <td>
                            <div className="submission-date">
                              {new Date(latestSub.submissionDate).toLocaleDateString()} 
                              <br />
                              {new Date(latestSub.submissionDate).toLocaleTimeString()}
                            </div>
                          </td>
                          <td>
                            {userGroup.submissions.length > 1 && (
                              <button
                                className="expand-btn"
                                onClick={() => setExpandedUsers({
                                  ...expandedUsers,
                                  [userGroup.userId]: !isExpanded
                                })}
                                title={isExpanded ? 'Hide versions' : 'Show all versions'}
                              >
                                {isExpanded ? '▼' : '▶'} {userGroup.submissions.length} versions
                              </button>
                            )}
                            {userGroup.submissions.length === 1 && (
                              <span className="single-submission">1 submission</span>
                            )}
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="action-btn view-btn"
                                onClick={() => viewSubmissionPDF(latestSub.id, latestSub.fileName)}
                                title="View Latest PDF"
                              >
                                <FaEye />
                              </button>
                              <button
                                className="action-btn download-btn-small"
                                onClick={() => downloadSubmissionPDF(latestSub.id, latestSub.fileName)}
                                title="Download Latest PDF"
                              >
                                <FaDownload />
                              </button>
                            </div>
                          </td>
                        </tr>
                        
                        {/* Expanded row showing all versions */}
                        {isExpanded && userGroup.submissions.length > 1 && (
                          <tr className="expanded-versions-row">
                            <td colSpan="11">
                              <div className="versions-container">
                                <h4>All Submitted Versions ({userGroup.submissions.length})</h4>
                                <div className="versions-grid">
                                  {userGroup.submissions.map((sub, idx) => (
                                    <div key={sub.id} className="version-card">
                                      <div className="version-header">
                                        <span className="version-number">
                                          v{idx + 1} {idx === 0 && <span className="latest-badge">✨ Latest</span>}
                                        </span>
                                        <span className="version-date">
                                          {new Date(sub.submissionDate).toLocaleDateString()}
                                        </span>
                                      </div>
                                      <div className="version-info">
                                        <code>{sub.fileName}</code>
                                        <div className="version-time">
                                          {new Date(sub.submissionDate).toLocaleTimeString()}
                                        </div>
                                      </div>
                                      <div className="version-actions">
                                        <button
                                          className="action-btn view-btn small"
                                          onClick={() => viewSubmissionPDF(sub.id, sub.fileName)}
                                          title="View PDF"
                                        >
                                          <FaEye />
                                        </button>
                                        <button
                                          className="action-btn download-btn-small small"
                                          onClick={() => downloadSubmissionPDF(sub.id, sub.fileName)}
                                          title="Download PDF"
                                        >
                                          <FaDownload />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SECTION 3: CONFERENCE REGISTRATION & PAYMENT DETAILS */}
        {filteredAndSortedPayments.length > 0 && (
          <div className="analytics-section full-width">
            <h2>Conference Registration & Payment Details ({filteredAndSortedPayments.length})</h2>
            
            {/* Filter Controls */}
            <div className="filter-controls">
              <input
                type="text"
                placeholder="Filter by Full Name..."
                value={payFilters.name}
                onChange={(e) => setPayFilters({...payFilters, name: e.target.value})}
                className="filter-input"
              />
              <input
                type="text"
                placeholder="Filter by Email..."
                value={payFilters.email}
                onChange={(e) => setPayFilters({...payFilters, email: e.target.value})}
                className="filter-input"
              />
              <input
                type="text"
                placeholder="Filter by Payment Status..."
                value={payFilters.status}
                onChange={(e) => setPayFilters({...payFilters, status: e.target.value})}
                className="filter-input"
              />
              <select 
                value={`${paySort.field}-${paySort.order}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setPaySort({ field, order });
                }}
                className="sort-select"
              >
                <option value="registrationDate-desc">Latest Registration First</option>
                <option value="registrationDate-asc">Oldest Registration First</option>
                <option value="name-asc">Name (A-Z)</option>
                <option value="totalAmount-desc">Highest Amount First</option>
                <option value="totalAmount-asc">Lowest Amount First</option>
                <option value="status-asc">Status (A-Z)</option>
              </select>
              <button onClick={() => setPayFilters({ name: '', email: '', status: '' })} className="clear-filter-btn">Clear Filters</button>
            </div>

            <div className="table-scroll">
              <table className="data-table full-table">
                <thead>
                  <tr>
                    <th>Full Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Participant Category</th>
                    <th>Registration Amount</th>
                    <th>Accommodation Amount</th>
                    <th>Total Amount</th>
                    <th>Payment Date</th>
                    <th>Transaction Details</th>
                    <th>Registration Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedPayments.map((payment, idx) => (
                    <tr key={idx}>
                      <td><strong>{payment.title} {payment.lastName}</strong></td>
                      <td>{payment.email}</td>
                      <td>{payment.phone}</td>
                      <td>{payment.participantCategory}</td>
                      <td>₹{payment.registrationAmount}</td>
                      <td>₹{payment.accommodationAmount || 0}</td>
                      <td><strong>₹{payment.totalAmount}</strong></td>
                      <td>{payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : '-'}</td>
                      <td title={payment.transactionDetails}>{payment.transactionDetails ? payment.transactionDetails.substring(0, 30) + '...' : '-'}</td>
                      <td>{new Date(payment.registrationDate).toLocaleDateString()}</td>
                      <td>
                        <div className="payment-action-buttons">
                          {payment.paymentProofFileName && (
                            <button 
                              className="payment-action-btn download-btn"
                              onClick={() => handleDownloadPaymentProof(payment.id, payment.paymentProofFileName)}
                              title="Download Payment Proof"
                            >
                              ⬇️ Download
                            </button>
                          )}
                          {payment.registrationStatus === 'pending' && (
                            <>
                              <button 
                                className="payment-action-btn verify-btn"
                                onClick={() => handleVerifyPayment(payment.id, 'verified')}
                                title="Mark as Verified"
                              >
                                ✓ Verify
                              </button>
                              <button 
                                className="payment-action-btn reject-btn"
                                onClick={() => handleVerifyPayment(payment.id, 'rejected')}
                                title="Reject Payment"
                              >
                                ✗ Reject
                              </button>
                            </>
                          )}
                          {(payment.registrationStatus === 'verified' || payment.registrationStatus === 'completed') && (
                            <button 
                              className="payment-action-btn complete-btn"
                              disabled
                              title="Already Approved"
                            >
                              ✓ Approved
                            </button>
                          )}
                          {payment.registrationStatus === 'rejected' && (
                            <button 
                              className="payment-action-btn reject-btn"
                              disabled
                              title="Rejected"
                            >
                              ✗ Rejected
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SECTION 4: HOSTEL ACCOMMODATIONS */}
        {filteredAndSortedAccommodations.length > 0 && (
          <div className="analytics-section full-width">
            <h2>Hostel Accommodations ({filteredAndSortedAccommodations.length})</h2>
            
            {/* Filter Controls */}
            <div className="filter-controls">
              <input
                type="text"
                placeholder="Filter by Full Name..."
                value={accFilters.name}
                onChange={(e) => setAccFilters({...accFilters, name: e.target.value})}
                className="filter-input"
              />
              <input
                type="text"
                placeholder="Filter by Email..."
                value={accFilters.email}
                onChange={(e) => setAccFilters({...accFilters, email: e.target.value})}
                className="filter-input"
              />
              <input
                type="text"
                placeholder="Filter by Status..."
                value={accFilters.status}
                onChange={(e) => setAccFilters({...accFilters, status: e.target.value})}
                className="filter-input"
              />
              <select 
                value={`${accSort.field}-${accSort.order}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setAccSort({ field, order });
                }}
                className="sort-select"
              >
                <option value="accommodationDate-desc">Latest Booking First</option>
                <option value="accommodationDate-asc">Oldest Booking First</option>
                <option value="name-asc">Name (A-Z)</option>
                <option value="accommodationAmount-desc">Highest Amount First</option>
                <option value="status-asc">Status (A-Z)</option>
              </select>
              <button onClick={() => setAccFilters({ name: '', email: '', status: '' })} className="clear-filter-btn">Clear Filters</button>
            </div>

            <div className="table-scroll">
              <table className="data-table full-table">
                <thead>
                  <tr>
                    <th>Full Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Check-in Date</th>
                    <th>Check-out Date</th>
                    <th>Days</th>
                    <th>Amount</th>
                    <th>Payment Proof</th>
                    <th>Transaction Details</th>
                    <th>Payment Date</th>
                    <th>Status</th>
                    <th>Booking Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedAccommodations.map((acc, idx) => (
                    <tr key={idx}>
                      <td><strong>{acc.title} {acc.lastName}</strong></td>
                      <td>{acc.email}</td>
                      <td>{acc.phone}</td>
                      <td>{new Date(acc.checkInDate).toLocaleDateString()}</td>
                      <td>{new Date(acc.checkOutDate).toLocaleDateString()}</td>
                      <td>{acc.numberOfDays}</td>
                      <td><strong>₹{acc.accommodationAmount}</strong></td>
                      <td>
                        {acc.paymentProofFileName ? (
                          <button
                            className="payment-action-btn download-btn"
                            onClick={() => handleDownloadAccommodationProof(acc.id, acc.paymentProofFileName)}
                            title={`Download: ${acc.paymentProofFileName}`}
                          >
                            <FaDownload /> {acc.paymentProofFileName}
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td title={acc.transactionDetails}>{acc.transactionDetails ? acc.transactionDetails.substring(0, 20) + '...' : '-'}</td>
                      <td>{acc.paymentDate ? new Date(acc.paymentDate).toLocaleDateString() : '-'}</td>
                      <td>
                        <span className={`payment-status-badge status-${acc.accommodationStatus}`}>
                          {acc.accommodationStatus}
                        </span>
                      </td>
                      <td>{new Date(acc.accommodationDate).toLocaleDateString()}</td>
                      <td>
                        <div className="payment-action-buttons">
                          {acc.accommodationStatus === 'pending' && (
                            <>
                              <button 
                                className="payment-action-btn verify-btn"
                                onClick={() => handleUpdateAccommodationStatus(acc.id, 'verified')}
                                title="Mark as Verified"
                              >
                                ✓ Verify
                              </button>
                              <button 
                                className="payment-action-btn reject-btn"
                                onClick={() => handleUpdateAccommodationStatus(acc.id, 'rejected')}
                                title="Reject"
                              >
                                ✗ Reject
                              </button>
                            </>
                          )}
                          {(acc.accommodationStatus === 'verified' || acc.accommodationStatus === 'completed') && (
                            <button 
                              className="payment-action-btn complete-btn"
                              disabled
                              title="Already Approved"
                            >
                              ✓ Approved
                            </button>
                          )}
                          {acc.accommodationStatus === 'rejected' && (
                            <button 
                              className="payment-action-btn reject-btn"
                              disabled
                              title="Rejected"
                            >
                              ✗ Rejected
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SECTION 5: OTHER SECTIONS (Statistics, Analytics, Download, etc.) */}

        {/* Statistics Cards */}
        {statistics && (
          <div className="stats-section">
            <h2>Registration Statistics</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{statistics.totalRegistrations}</div>
                <div className="stat-label">Total Registrations</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{statistics.totalWithSubmissions}</div>
                <div className="stat-label">With Submissions</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{statistics.submissionCount}</div>
                <div className="stat-label">Total Submissions</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">
                  {statistics.totalRegistrations > 0
                    ? ((statistics.totalWithSubmissions / statistics.totalRegistrations) * 100).toFixed(1)
                    : 0}%
                </div>
                <div className="stat-label">Submission Rate</div>
              </div>
            </div>
          </div>
        )}

        {/* Registrations by Title */}
        {statistics && statistics.registrationsByTitle.length > 0 && (
          <div className="analytics-section">
            <h2>Registrations by Title</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Count</th>
                  <th>Percentage</th>
                </tr>
              </thead>
              <tbody>
                {statistics.registrationsByTitle.map((item, idx) => (
                  <tr key={idx}>
                    <td>{item.title}</td>
                    <td>{item.count}</td>
                    <td>
                      {((item.count / statistics.totalRegistrations) * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Top Affiliations */}
        {analytics && analytics.topAffiliations.length > 0 && (
          <div className="analytics-section">
            <h2>Top Affiliations</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Affiliation</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {analytics.topAffiliations.map((item, idx) => (
                  <tr key={idx}>
                    <td>{item.affiliation}</td>
                    <td>{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Recent Registrations */}
        {analytics && analytics.recentRegistrations.length > 0 && (
          <div className="analytics-section">
            <h2>Recent Registrations</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Title</th>
                  <th>Registration Date</th>
                </tr>
              </thead>
              <tbody>
                {analytics.recentRegistrations.map((item, idx) => (
                  <tr key={idx}>
                    <td><strong>{item.lastName} {item.nameInitials}</strong></td>
                    <td>{item.email}</td>
                    <td>{item.phone || '-'}</td>
                    <td>{item.title}</td>
                    <td>{new Date(item.registrationDate).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Download Section */}
        <div className="download-section">
          <h2>Export Data</h2>
          <div className="download-buttons">
            <button className="download-btn" onClick={handleDownloadCSV}>
              <FaDownload /> Download All Registrations as CSV
            </button>
            <button className="download-btn" onClick={handleDownloadSubmissionsCSV}>
              <FaDownload /> Download All Submissions Info as CSV
            </button>
            <button className="download-btn" onClick={handleDownloadPaymentCSV}>
              <FaDownload /> Download Payment Details as CSV
            </button>
            <button className="download-btn" onClick={handleDownloadAccommodationsCSV}>
              <FaDownload /> Download Accommodations as CSV
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
